/**
 * Onboarding step 2 (screenshots/49): address → ward, entirely in the browser, the same way the public site's
 * "find my ward" works (site/js/app.js): the geocoder, then point-in-polygon on /data/wards.geojson. The street
 * address is never sent to our server; onboardingPayload() sends only the ward number.
 */
import type { WardLookup } from "../account/types";

type Ring = [number, number][];
interface WardFeature { properties: { ward: number | string }; geometry: { type: "Polygon" | "MultiPolygon"; coordinates: Ring[] | Ring[][] } }

const cache = new Map<string, Promise<unknown>>();
const data = <T,>(name: string) => {
  if (!cache.has(name)) cache.set(name, fetch(`/data/${name}`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  return cache.get(name) as Promise<T | null>;
};

export function inRing([x, y]: [number, number], ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!, [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The ward a [lon, lat] point is in (holes excluded), or null outside Chicago. */
export function wardAt(pt: [number, number], features: WardFeature[]): number | null {
  for (const f of features) {
    const polys = (f.geometry.type === "Polygon" ? [f.geometry.coordinates] : f.geometry.coordinates) as Ring[][];
    if (polys.some((poly) => inRing(pt, poly[0]!) && !poly.slice(1).some((h) => inRing(pt, h)))) return Number(f.properties.ward);
  }
  return null;
}

export async function lookupWard(address: string): Promise<WardLookup | null> {
  const q = address.trim();
  if (q.length < 4) return null;
  // Same geocoder and Chicago bounding box as the public site.
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&bounded=1&viewbox=-87.95,42.03,-87.52,41.64&q=${encodeURIComponent(`${q}, Chicago, IL`)}`;
  const [hit] = ((await fetch(url, { headers: { accept: "application/json" } }).then((r) => (r.ok ? r.json() : [])).catch(() => [])) as { lat: string; lon: string }[]);
  if (!hit) return null;
  const [gj, alds, profiles] = await Promise.all([
    data<{ features: WardFeature[] }>("wards.geojson"),
    data<Record<string, { personId: string; name: string; photo?: string }>>("alderpersons.json"),
    data<Record<string, { neighborhoods?: string[] }>>("profiles.json"),
  ]);
  const ward = gj ? wardAt([Number(hit.lon), Number(hit.lat)], gj.features) : null;
  if (!ward) return null;
  const a = alds?.[String(ward)];
  return { number: ward, alderpersonId: a?.personId ?? "", alderpersonName: a?.name ?? "", photoUrl: a?.photo, neighborhood: profiles?.[String(ward)]?.neighborhoods?.[0] };
}
